<template>
  <div class="section-creator-container center-text px-6 pb-6">
    <div class="d-flex justify-end">
      <v-icon @click.stop="closeSectionCreator" class="mr-n2">
        close
      </v-icon>
    </div>
    <h2 class="pb-3">
      {{ $t('journal.sectionCreation.title') }}
    </h2>
    <v-progress-circular
      indeterminate
      v-if="loadingSections"
      :size="30"
      :width="4"
    />
    <split-tabs
      :items="sections"
      :toggle-vertical-split-enabled="verticalSplitSectionCreator"
      v-else
      v-slot="props"
      class="border-grey"
      @add="addSection"
      @remove="removeSection"
    >
      <perfect-scrollbar
        :options="{suppressScrollX: true}"
        class="section-creator-scrollbar background-grey"
      >
        <div class="background-grey px-3 pt-3">
          <div class="prompt-option pa-6">
            <AdvancedSearch
              :existing-search-infos="props.item.existingSearchInfos"
              @submit="generateRecommendation(props.index, props.split, $event)"
            />
          </div>
        </div>
        <div
          class="section-creator-results px-3"
          :class="(props.item.recommendations.length === 0) ? 'center-no-results': ''"
        >
          <div v-if="props.item.recommendations.length > 0" class="d-flex justify-center pl-6 py-2">
            <span class="font-subtitle">
              {{ $t('journal.search.results') }}
            </span>
          </div>
          <PromptRecommendationsFetcher
            v-show="props.item.recommendations.length === 0"
            :ref="`promptRecommendationsFetcher-${props.index}-${props.split}`"
          />
          <DocumentCardIterator :items="props.item.recommendations">
            <template v-slot:item="{ item }">
              <HorizontalCard
                :key="item.id"
                :document-data="item"
                :show-evaluation="false"
                :allow-adding-to-reflection="true"
              />
            </template>
          </DocumentCardIterator>
        </div>
      </perfect-scrollbar>
    </split-tabs>
    <div>
      <v-row no-gutters class="justify-end mt-6">
        <div class="d-flex justify-start" style="width: 600px;">
          <v-text-field
            dense
            v-model="sectionName"
            :label="$t('journal.search.sectionNamePlaceholder')"
            :disabled="!sectionNameEnabled()"
          />
          <v-btn
            text
            plain
            color="primary"
            :disabled="!saveEnabled"
            @click.exact="saveSection"
            @click.stop.prevent
          >
            {{ saveButtonLabel() }}
          </v-btn>
          <div class="search-save-status">
            <v-progress-circular
              v-if="saveInProgress"
              :size="16"
              :width="2"
              indeterminate
            />
            <v-icon
              v-if="saveSuceeded"
              color="green"
            >
              check_circle
            </v-icon>
            <v-icon
              v-if="saveFailed"
              color="red"
            >
              cancel
            </v-icon>
          </div>
        </div>
      </v-row>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { createJournalSection, updateJournalSection, getJournalSections } from '../../search/SearchUtils';
import SplitTabs from './SplitTabs';
import DocumentCardIterator from '../../cards/DocumentCardIterator';
import HorizontalCard from '../../cards/documentCards/HorizontalCard';
import AdvancedSearch from '../../search/AdvancedSearch';
import { types } from '../../../store/rootTypes';
import PromptRecommendationsFetcher from '../../recommendations/PromptRecommendationsFetcher';

export default {
  name: 'SectionCreator',
  props: {
    existingSectionInfos: {
      type: Object,
      default: undefined,
    },
    defaultSectionName: {
      type: String,
      default: '',
    },
    reflectionId: {
      type: String,
      default: undefined,
    },
    headerId: {
      type: String,
      default: undefined,
    },
  },
  components: {
    DocumentCardIterator,
    HorizontalCard,
    AdvancedSearch,
    PromptRecommendationsFetcher,
    SplitTabs,
  },
  data: () => ({
    advancedSearchState: [{}],
    loadingSections: false,
    text: '',
    sectionName: '',
    searchInProgress: false,
    saveInProgress: false,
    saveSuceeded: false,
    saveFailed: false,
    sectionPanels: [],
    sections: [],
    saveEnabled: false,
  }),
  async created() {
    await this.inititalize();
  },
  computed: {
    ...mapGetters({
      userId: types.authentication.getters.userId,
      organizationId: types.authentication.getters.organizationId,
      verticalSplitSectionCreator: types.featureSwitches.getters.verticalSplitSectionCreator,
    }),
  },
  methods: {
    async inititalize() {
      if (this.fromExistingSearch()) {
        this.loadingSections = true;
        const journalSections = await getJournalSections(this.existingJournalId());

        this.sectionName = this.existingSectionInfos.title;
        this.sections = journalSections.map((section) => ({
          searchId: section.searchId,
          recommendationCounts: section.recommendationCounts,
          existingSearchInfos: {
            section,
          },
          recommendations: [],
        }));
        this.loadingSections = false;
      } else {
        this.sectionName = this.defaultSectionName;
        this.sections = [{
          existingSearchInfos: {},
          recommendations: [],
        }];
      }
      this.saveEnabled = this.computeSaveEnabled();
    },
    removeSection(index) {
      if (this.sections.length === 1) return;

      this.sections.splice(index, 1);
      this.sectionPanels = [this.sections.length - 1];
    },
    addSection() {
      this.sections.push(this.defaultSection());
      this.advancedSearchState.push({});
      this.sectionPanels = [this.sections.length - 1];
    },
    closeSectionCreator() {
      this.$emit('close-section-creator');
    },
    fromExistingSearch() {
      return this.existingSectionInfos !== undefined;
    },
    async generateRecommendation(index, split, preparedSearchable) {
      this.clearSection(index);
      this.resetSaveStatus();

      this.searchInProgress = true;
      const fetcherRefName = `promptRecommendationsFetcher-${index}-${split}`;
      const fetcher = this.$refs[fetcherRefName];
      const response = await fetcher.fetch(preparedSearchable);

      this.sections[index].searchId = response.searchId;
      this.sections[index].recommendations = response.recommendations;
      this.sections[index].recommendationCount = preparedSearchable.searchQuery.max_results;

      this.advancedSearchState[index] = {};
      this.searchInProgress = false;
      this.saveEnabled = this.computeSaveEnabled();
    },
    saveButtonLabel() {
      if (this.fromExistingSearch()) {
        return this.$t('journal.sectionCreation.update');
      }
      return this.$t('journal.sectionCreation.create');
    },
    resetSaveStatus() {
      this.saveInProgress = false;
      this.saveSuceeded = false;
      this.saveFailed = false;
    },
    computeSaveEnabled() {
      const sectionWithSearchId = this.sections.filter(section => section.searchId);

      return (
        this.sectionName
        && sectionWithSearchId.length === this.sections.length
        && this.sectionName !== ''
        && !this.saveInProgress
        && (!this.saveSuceeded || this.fromExistingSearch())
      );
    },
    sectionNameEnabled() {
      return !this.saveInProgress && (this.fromExistingSearch || !this.saveSucceeded);
    },
    existingJournalId() {
      if (this.existingSectionInfos) {
        return this.existingSectionInfos.journalId;
      }
      return undefined;
    },
    async saveSection() {
      if (!this.computeSaveEnabled()) return;
      this.resetSaveStatus();
      this.saveInProgress = true;
      const searchIds = this.sections.map(section => section.searchId);
      const recommendationCounts = this.sections.map(section => section.recommendationCount);

      try {
        if (this.fromExistingSearch()) {
          await updateJournalSection(
            searchIds,
            recommendationCounts,
            this.sectionName,
            this.existingJournalId(),
          );
          this.closeSectionCreator();
        } else {
          await createJournalSection(searchIds, recommendationCounts, this.sectionName, this.reflectionId, this.headerId);
        }
        this.saveInProgress = false;
        this.saveSuceeded = true;
      } catch (error) {
        console.error('Error while trying to save: ', error);
        this.saveInProgress = false;
        this.saveFailed = true;
      }
    },
    clearSection(index) {
      this.sections[index].recommendations = [];
    },
    defaultSection() {
      return {
        recommendations: [],
        existingSearchInfos: {},
        recommendationCount: 8,
      };
    },
  },
  watch: {
    sectionName() {
      this.saveEnabled = this.computeSaveEnabled();
    },
    async existingSectionInfos() {
      await this.inititalize();
    },
  },
};
</script>
<style scoped>
.section-creator-container {
  background-color: white;
  padding: 12px;
}
.prompt-option {
  background-color: white;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  border-radius: 8px;
}
.center-text {
  text-align: center;
}
.center-no-results {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.section-creator-scrollbar {
  max-height: 60vh;
  min-height: 60vh;
  width: 100%;
}
.background-grey {
  background-color: #F6F5FA;
}
.section-creator-results {
  min-height: 150px;
}
.search-save-status {
  width: 32px;
  display: flex;
  text-align: center;
  align-items: center;
  margin-bottom: 12px;
}
.font-subtitle {
  font-size: 18px;
  font-weight: 500;
}
.border-grey {
  border: 1px solid #E0E0E0;
  border-radius: 5px;
}
</style>
